import { h } from '@omni-torn/ui';
import type { TornRefAnswer, TornRefKind } from '@omni-torn/shared/torn-ref';
import { server } from './session';
import { ago, problem } from './ui';

/**
 * Torn's public lists as the Omni-Torn server keeps them (GET /api/torn/<kind>, apps/api/src/torn-data.ts — read with the
 * server's own key, so these cost the player's key nothing), for the website's Torn pages (pages/torn.ts,
 * pages/ranked-wars.ts, pages/factions.ts).
 */

/** This visit's copies: kept until the server says it asks Torn again. */
const kept = new Map<string, TornRefAnswer>();

export async function ref<K extends TornRefKind>(kind: K, arg: string | null = null): Promise<TornRefAnswer<K>> {
  const id = `${kind}:${arg ?? ''}`;
  const k = kept.get(id);
  if (k && Date.now() < k.next) return k as TornRefAnswer<K>;
  const a = await server<TornRefAnswer<K>>('GET', `/api/torn/${kind}${arg ? `?arg=${encodeURIComponent(arg)}` : ''}`);
  kept.set(id, a);
  return a;
}

/** "From Torn, 12m ago" (and when Torn couldn't be asked: the last copy). */
export function fromTorn(...answers: Array<TornRefAnswer | null | undefined>): HTMLElement {
  const list = answers.filter((a): a is TornRefAnswer => !!a);
  const oldest = Math.min(...list.map((a) => a.at));
  const stale = list.some((a) => a.stale);
  return h('p', { class: 'muted small ref-note', 'data-ref-note': '' }, `From Torn’s API, ${Number.isFinite(oldest) ? ago(oldest) : '—'}.`, stale ? ' Torn couldn’t be asked just now — this is the last copy.' : '');
}

/** What went wrong, with a "Try again" (Torn busy, a hiccup). */
export function oops(err: unknown, again: () => unknown): HTMLElement {
  return h('div', { class: 'ref-problem' }, problem(err), h('button', { type: 'button', class: 'btn-small', onclick: () => void again() }, 'Try again'));
}
